"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Toaster } from "sonner";
import { dictionaries, t, type DictKey, type Locale } from "@/lib/i18n/dictionary";
import {
  LOCALE_COOKIE,
  LOCALE_KEY,
  THEME_KEY,
  dismissBootSplash,
  readStoredPrefs,
  type Theme,
} from "@/lib/prefs";

type Prefs = {
  locale: Locale;
  theme: Theme;
  setLocale: (locale: Locale) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  toggleLocale: () => void;
  t: (key: DictKey, vars?: Record<string, string | number>) => string;
  dir: "rtl" | "ltr";
};

const PrefsContext = createContext<Prefs | null>(null);

declare global {
  interface Window {
    __MYAPI_PREFS__?: { locale: Locale; theme: Theme };
    __MYAPI_CLEAR_BOOT__?: () => void;
  }
}

function applyDom(locale: Locale, theme: Theme) {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = locale === "he" ? "rtl" : "ltr";
  root.classList.toggle("dark", theme === "dark");
  document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

export function AppProviders({ children }: { children: React.ReactNode }) {
  // Keep SSR/client first paint aligned; real prefs applied in useEffect under the splash.
  const [locale, setLocaleState] = useState<Locale>("en");
  const [theme, setThemeState] = useState<Theme>("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const next = window.__MYAPI_PREFS__ ?? readStoredPrefs();
    if (!localStorage.getItem(LOCALE_KEY)) localStorage.setItem(LOCALE_KEY, next.locale);
    if (!localStorage.getItem(THEME_KEY)) localStorage.setItem(THEME_KEY, next.theme);
    setLocaleState(next.locale);
    setThemeState(next.theme);
    applyDom(next.locale, next.theme);
    setMounted(true);

    // Drop splash after the locale commit paints — with a hard failsafe from the boot script.
    const reveal = () => {
      dismissBootSplash();
      window.__MYAPI_CLEAR_BOOT__?.();
    };
    requestAnimationFrame(() => requestAnimationFrame(reveal));
  }, []);

  const setLocale = useCallback((next: Locale) => {
    localStorage.setItem(LOCALE_KEY, next);
    setLocaleState(next);
    const th = (localStorage.getItem(THEME_KEY) as Theme) || "light";
    applyDom(next, th);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(THEME_KEY, next);
    setThemeState(next);
    const loc = (localStorage.getItem(LOCALE_KEY) as Locale) || "en";
    applyDom(loc, next);
  }, []);

  const value = useMemo<Prefs>(
    () => ({
      locale,
      theme,
      setLocale,
      setTheme,
      toggleTheme: () => setTheme(theme === "dark" ? "light" : "dark"),
      toggleLocale: () => setLocale(locale === "he" ? "en" : "he"),
      t: (key, vars) => t(locale, key, vars),
      dir: locale === "he" ? "rtl" : "ltr",
    }),
    [locale, theme, setLocale, setTheme],
  );

  return (
    <PrefsContext.Provider value={value}>
      <div id="app-root" className="app-root flex min-h-full flex-1 flex-col">
        {children}
      </div>
      {mounted && (
        <Toaster
          position="top-center"
          dir={locale === "he" ? "rtl" : "ltr"}
          richColors
          closeButton
          theme={theme}
        />
      )}
    </PrefsContext.Provider>
  );
}

export function usePrefs() {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error("usePrefs must be used within AppProviders");
  return ctx;
}

export function useDict() {
  const { locale, t: translate } = usePrefs();
  return { locale, t: translate, dict: dictionaries[locale] };
}
